import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiService from '../../utils/apiService';

function poolMarketsToPayload(markets) {
    return Object.values(markets || {}).map((row) => ({
        poolAddress: row.poolAddress,
        volume24h: row.volume24h ?? 0,
        priceUsd: row.priceUsd ?? 0,
        priceChange24h: row.priceChange24h ?? 0,
        liquidity: row.liquidity ?? 0,
        message: 'Pool data fetched successfully',
    }));
}

/** Dashboard batch fetch via Dex pair addresses (no Jupiter fallback). */
export const fetchDashboardPoolMarkets = createAsyncThunk(
    'tokenVolume/fetchDashboardPoolMarkets',
    async (
        {
            pairs,
            includeVolume = true,
            forcePrice = false,
            forceVolume = false,
        } = {},
        { rejectWithValue },
    ) => {
        try {
            const list = [...new Set((pairs || []).map(String).filter(Boolean))];
            if (!list.length) return [];

            const data = await apiService.getPoolMarket(list, {
                includeVolume,
                forcePrice,
                forceVolume,
            });

            return poolMarketsToPayload(data?.markets);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    },
);

/** @deprecated Dashboard uses fetchDashboardPoolMarkets; kept for legacy callers. */
export const fetchDashboardTokenMarkets = createAsyncThunk(
    'tokenVolume/fetchDashboardTokenMarkets',
    async () => [],
);

export const fetchTokenVolume = createAsyncThunk(
    'tokenVolume/fetchTokenVolume',
    async (poolAddress, { dispatch }) => {
        const rows = await dispatch(
            fetchDashboardPoolMarkets({
                pairs: [poolAddress],
                includeVolume: true,
            }),
        ).unwrap();
        return (
            rows[0] || {
                poolAddress,
                volume24h: 0,
                priceUsd: 0,
                priceChange24h: 0,
                liquidity: 0,
                message: 'Pool not found or no trading data',
            }
        );
    },
);

export const fetchMultipleTokenVolumes = createAsyncThunk(
    'tokenVolume/fetchMultipleTokenVolumes',
    async (poolAddresses, { dispatch }) =>
        dispatch(
            fetchDashboardPoolMarkets({
                pairs: poolAddresses,
                includeVolume: true,
            }),
        ).unwrap(),
);

const initialState = {
    tokenVolumes: {},
    loading: 'idle',
    error: null
};

const tokenVolumeSlice = createSlice({
    name: 'tokenVolume',
    initialState,
    reducers: {
        resetTokenVolumes(state) {
            state.tokenVolumes = {};
            state.loading = 'idle';
            state.error = null;
        },
        clearTokenVolume(state, action) {
            const poolAddress = action.payload;
            delete state.tokenVolumes[poolAddress];
        }
    },
    extraReducers: (builder) => {
        const applyMarketRows = (state, rows) => {
            for (const volumeData of rows) {
                if (volumeData?.poolAddress) {
                    state.tokenVolumes[volumeData.poolAddress] = volumeData;
                }
            }
        };

        builder
            .addCase(fetchDashboardPoolMarkets.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(fetchDashboardPoolMarkets.fulfilled, (state, action) => {
                state.loading = 'idle';
                applyMarketRows(state, action.payload);
                state.error = null;
            })
            .addCase(fetchDashboardPoolMarkets.rejected, (state, action) => {
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
            })
            .addCase(fetchTokenVolume.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(fetchTokenVolume.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.tokenVolumes[action.payload.poolAddress] = action.payload;
                state.error = null;
            })
            .addCase(fetchTokenVolume.rejected, (state, action) => {
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
            })
            .addCase(fetchMultipleTokenVolumes.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(fetchMultipleTokenVolumes.fulfilled, (state, action) => {
                state.loading = 'idle';
                applyMarketRows(state, action.payload);
                state.error = null;
            })
            .addCase(fetchMultipleTokenVolumes.rejected, (state, action) => {
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
            });
    },
});

export const { resetTokenVolumes, clearTokenVolume } = tokenVolumeSlice.actions;

export const selectPoolMarket = (state, poolAddress) =>
    state.tokenVolume.tokenVolumes[poolAddress];

/** @deprecated Use selectPoolMarket — keyed by poolAddress now. */
export const selectTokenVolume = selectPoolMarket;

export const selectAllTokenVolumes = (state) =>
    state.tokenVolume.tokenVolumes;

export const selectTokenVolumeLoading = (state) =>
    state.tokenVolume.loading;

export const selectTokenVolumeError = (state) =>
    state.tokenVolume.error;

export default tokenVolumeSlice.reducer;
